import React, { useState, useMemo, useCallback } from "react";
import { Button, Spinner, Table } from "react-bootstrap";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import "./index.css";
import { MdDelete, MdEdit } from "react-icons/md";
/* =========================
   Generic Helpers
========================= */
const isNil = (v) => v == null;
const hasOwn = (obj, k) => Object.prototype.hasOwnProperty.call(obj || {}, k);
const get = (obj, path) => {
  if (typeof path === "function") return path(obj);
  if (!path) return undefined;
  const parts = String(path).split(".");
  let cur = obj;
  for (const p of parts) {
    if (isNil(cur)) return undefined;
    cur = cur[p];
  }
  return cur;
};
const pick = (...vals) => vals.find((v) => !isNil(v) && v !== "") ?? undefined;
const toNumber = (v) =>
  v === "" || v == null || Number.isNaN(+v) ? undefined : +v;
const toDate = (v) => (v ? new Date(v) : undefined);
const fmt = (v) => (v == null || v === "" ? "—" : String(v));

/* kind & url helpers (customize as per your backend) */
// const buildFileUrl = (rawPath) => {
//   // If your backend already returns absolute URLs, just return rawPath.
//   // Otherwise, prefix with your static host:
//   return String(rawPath).startsWith("http") ? rawPath : `${location.origin}/${String(rawPath).replace(/^\/+/, "")}`;
// };
const kindFromPath = (url) => {
  const u = (url || "").toLowerCase();
  if (/\.(png|jpe?g|webp|gif|bmp|svg)(\?|#|$)/.test(u)) return "image";
  if (/\.(pdf)(\?|#|$)/.test(u)) return "pdf";
  return "file";
};

/* dimension formatter */
const formatDim = (w, h, unit) => {
  if (isNil(w) && isNil(h)) return "—";
  const wu = isNil(w) ? "—" : String(w);
  const hu = isNil(h) ? "—" : String(h);
  const su = unit ? ` ${unit}` : "";
  return `${wu} × ${hu}${su}`;
};

/* variables helpers */
const has = (obj, k) => hasOwn(obj || {}, k);
const toEntries = (vmap) =>
  Object.entries(vmap || {}).map(([key, val]) => ({ key, val: val ?? {} }));
const summarizeVar = (key, val) => {
  const p = [];
  if (has(val, "x")) p.push(`x:${val.x}`);
  if (has(val, "y")) p.push(`y:${val.y}`);
  if (has(val, "font_size") || has(val, "fontSize"))
    p.push(`fs:${val.font_size ?? val.fontSize}`);
  return `${key}(${p.join(", ")})`;
};

/* multi-file helper (supports your mixed shapes) */
const getSleeveFilePaths = (s) => {
  const raw = pick(s.file_path, s.file, s.file_url, s.path, s.filePath);
  if (!raw) return [];
  if (typeof raw === "string") return [raw];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === "object") {
    return Object.values(raw).filter(Boolean);
  }
  return [];
};

/* =========================
   Sorting (type-aware)
========================= */
function compareAuto(a, b) {
  // Try number
  const na = toNumber(a);
  const nb = toNumber(b);
  if (!isNil(na) && !isNil(nb)) return na - nb;

  // Try date
  const da = toDate(a);
  const db = toDate(b);
  if (da instanceof Date && !isNaN(+da) && db instanceof Date && !isNaN(+db)) {
    return +da - +db;
  }

  // Fallback string (case-insensitive)
  const sa = (a ?? "").toString().toLowerCase();
  const sb = (b ?? "").toString().toLowerCase();
  if (sa < sb) return -1;
  if (sa > sb) return 1;
  return 0;
}

const TYPE_COMPARATORS = {
  number: (a, b) =>
    (toNumber(a) ?? Number.NEGATIVE_INFINITY) -
    (toNumber(b) ?? Number.NEGATIVE_INFINITY),
  text: (a, b) => {
    const sa = (a ?? "").toString().toLowerCase();
    const sb = (b ?? "").toString().toLowerCase();
    if (sa < sb) return -1;
    if (sa > sb) return 1;
    return 0;
  },
  date: (a, b) =>
    (toDate(a)?.getTime() ?? -Infinity) - (toDate(b)?.getTime() ?? -Infinity),
  auto: compareAuto,
};

const SimpleTable = ({
  data = [],
  columns = [],
  loading = false,
  keyField = "id",
  emptyMessage = "No records found",
  responsive,
  size,
  tableProps = {},
  headerSticky = true,
  onEdit = () => {},
  onDelete = () => {},
}) => {
  const [sort, setSort] = useState({ id: null, dir: "asc" });

  const onSort = useCallback(
    (col) => {
      if (col.sortable === false) return;
      const id = col.id || col.accessor || col.header;
      setSort((prev) => {
        if (prev.id === id) {
          // toggle dir
          const ndir = prev.dir === "asc" ? "desc" : "asc";
          return { id, dir: ndir };
        }
        return { id, dir: "asc" };
      });
    },
    [setSort]
  );

  const columnsWithIds = useMemo(
    () =>
      columns.map((c, i) => ({
        sortable: c.sortable ?? Boolean(c.accessor),
        sortType: c.sortType ?? "auto",
        ...c,
        _id: c.id || c.accessor || c.header || `col_${i}`,
      })),
    [columns]
  );

  const sorted = useMemo(() => {
    if (!sort.id) return data;
    const col = columnsWithIds.find((c) => c._id === sort.id);
    if (!col) return data;

    // Resolve comparator
    let cmp;
    if (typeof col.sortType === "function") cmp = col.sortType;
    else cmp = TYPE_COMPARATORS[col.sortType] || TYPE_COMPARATORS.auto;

    const arr = [...data];
    arr.sort((ra, rb) => {
      const va = col.accessor ? get(ra, col.accessor) : undefined;
      const vb = col.accessor ? get(rb, col.accessor) : undefined;
      const r = cmp(va, vb);
      return sort.dir === "asc" ? r : -r;
    });
    return arr;
  }, [data, sort, columnsWithIds]);
  return (
    // <div className="main-tbl-cover">
    <div className="table-cover">
      {loading ? (
        <div className="text-center py-3 h-100 d-flex align-items-center justify-content-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Table
            hover
            bordered
            className="position-relative z-1 mb-0"
            responsive={responsive}
            size={size}
          >
            <thead className="sticky-top shadow-md">
              <tr>
                {columnsWithIds.map((col) => {
                  const isActive = sort.id === col._id;
                  const canSort = col.sortable !== false;
                  const label =
                    typeof col.header === "string" ? (
                      <span className="text-nowrap">{col.header}</span>
                    ) : (
                      col.header
                    );

                  const headerInner = (
                    <div className="d-flex align-items-center gap-2">
                      {label}
                      <div className="d-flex flex-column">
                        <button
                          className={`sort-btn border-none ${
                            isActive && sort?.dir === "asc"
                              ? "active"
                              : "inactive"
                          }`}
                        >
                          <FaSortUp />
                        </button>
                        <button
                          className={`sort-btn border-none ${
                            isActive && sort?.dir === "desc"
                              ? "active"
                              : "inactive"
                          }`}
                        >
                          <FaSortDown />
                        </button>
                      </div>
                      {/* {isActive && (
                        <span aria-hidden style={{ fontSize: 12 }}>
                          {sort.dir === "asc" ? "▲" : "▼"}
                        </span>
                      )} */}
                    </div>
                  );

                  return (
                    <th
                      key={col._id}
                      style={{ minWidth: col.minWidth }}
                      className={`${col.className}`}
                      onClick={canSort ? () => onSort(col) : undefined}
                      role={canSort ? "button" : undefined}
                      title={canSort ? "Sort" : undefined}
                    >
                      {canSort ? headerInner : label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, idx) => (
                <tr key={row?.[keyField] ?? idx}>
                  {columnsWithIds.map((col) => {
                    const value = col.accessor
                      ? get(row, col.accessor)
                      : undefined;
                    if (col.isActions) {
                      return (
                        <td
                          key={col._id}
                          style={{ minWidth: col.minWidth }}
                          className={`text-nowrap ${col.className || ""}`}
                        >
                          <button
                            type="button"
                            size="sm"
                            className="table-btn me-2 edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(row);
                            }}
                            title="edit"
                          >
                            <MdEdit />
                          </button>
                          <button
                            size="sm"
                            type="button"
                            className="table-btn delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(row);
                            }}
                            title="delete"
                          >
                            <MdDelete />
                          </button>
                        </td>
                      );
                    }

                    const content = col.cell
                      ? col.cell(value, row, idx)
                      : fmt(value);
                    return (
                      <td
                        key={col._id}
                        style={{ minWidth: col.minWidth }}
                        className={col.className}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {sorted.length === 0 && (
                <tr>
                  <td
                    colSpan={columnsWithIds.length}
                    className="text-center text-muted"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </>
      )}
    </div>
    // </div>
  );
};

export default SimpleTable;
